import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "../ui/index.js";
import { SetupStepHeader } from "./SetupStepHeader.js";
import {
  formatCallDeliveryNumber,
  TRAINING_SUB_STEPS,
  TRAINING_WATCHED_EPSILON,
  TRAINING_WATCHED_THRESHOLD,
  trainingEmbedUrl,
} from "./setupSteps.js";
import { loadWistiaEmbedScript, onWistiaVideoReady, type WistiaVideo } from "../../lib/wistiaPlayer.js";
import { markTrainingStepWatched, readWatchedTrainingSteps } from "./setupStorage.js";

function SubStepRail({
  index,
  watched,
  allowSkip,
  onSelect,
}: {
  index: number;
  watched: string[];
  /** Lifts the "only as far as you've been" rule for impersonating staff. */
  allowSkip: boolean;
  onSelect: (next: number) => void;
}) {
  return (
    <ol className="flex flex-wrap gap-2" aria-label="Training videos">
      {TRAINING_SUB_STEPS.map((step, position) => {
        const isWatched = watched.includes(step.id);
        const isCurrent = position === index;
        // Everything already watched stays reachable; anything past the first
        // unwatched video is not, so the sequence can't be skipped.
        const reachable = allowSkip || isWatched || isCurrent;
        return (
          <li key={step.id}>
            <button
              type="button"
              onClick={() => reachable && onSelect(position)}
              disabled={!reachable}
              aria-current={isCurrent ? "step" : undefined}
              title={reachable ? step.title : "Watch the earlier videos first"}
              className={`focus-ring grid h-10 w-10 place-items-center rounded-full border-2 text-sm font-extrabold transition ${
                isCurrent
                  ? "border-accent-strong bg-accent-strong text-on-accent"
                  : isWatched
                    ? "border-status-active-border bg-status-active-bg text-status-active-text"
                    : "cursor-not-allowed border-border-default bg-surface-overlay text-content-muted"
              }`}
            >
              <span className="sr-only">{`Video ${position + 1}: ${step.title}`}</span>
              <span aria-hidden>{position + 1}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export interface DialerTrainingStepProps {
  agentId: string | null;
  complete?: boolean;
  /**
   * Shown on the "set your call delivery number" video — that's the moment the
   * agent needs it, rather than one step later on calibration.
   */
  callDeliveryNumber?: string | null;
  /**
   * Staff impersonating an agent can move through the videos freely — the
   * watch gate and the sequential rail are both lifted.
   */
  allowSkip?: boolean;
  /** Fired after each sub-step is marked watched, so the host can re-read progress. */
  onProgressChange?: () => void;
  /** Fired once the last video is finished. */
  onStepComplete?: () => void;
}

/**
 * How long to wait for Wistia's player to report in before giving up on
 * tracking. Short enough not to strand anyone, long enough for a slow load.
 */
const PLAYER_TIMEOUT_MS = 12_000;

/**
 * Step 3 of setup: the nine dialer training videos, one navigable sub-step at a
 * time. Next unlocks once Wistia reports the video actually watched — measured
 * in unique seconds, so scrubbing to the end doesn't count. If the player never
 * reports in (script blocked, network down) the gate opens rather than
 * stranding the agent on a video they can't get past.
 */
export function DialerTrainingStep({
  agentId,
  complete,
  callDeliveryNumber,
  allowSkip = false,
  onProgressChange,
  onStepComplete,
}: DialerTrainingStepProps) {
  const [index, setIndex] = useState(0);
  const [watched, setWatched] = useState<string[]>([]);
  const [percentWatched, setPercentWatched] = useState(0);
  const [watchedThrough, setWatchedThrough] = useState(false);
  // null while we're still waiting to hear from the player; false once we've
  // given up on it and opened the gate.
  const [tracking, setTracking] = useState<boolean | null>(null);
  const videoRef = useRef<WistiaVideo | null>(null);

  const step = TRAINING_SUB_STEPS[index];
  const alreadyWatched = watched.includes(step.id);
  const isLast = index === TRAINING_SUB_STEPS.length - 1;
  const canAdvance = allowSkip || alreadyWatched || watchedThrough || tracking === false;

  // Watch the player for this sub-step and unlock once every second of the
  // video has been watched. Both `secondchange` and `percentwatchedchanged`
  // drive the same check, so the gate still works if either one turns out not
  // to fire on iframe embeds. `end` re-runs the check rather than unlocking on
  // its own — it also fires when someone drags the scrubber to the end, which
  // is exactly what the gate is here to catch.
  useEffect(() => {
    videoRef.current = null;
    setPercentWatched(0);
    setWatchedThrough(false);
    setTracking(null);
    if (alreadyWatched) return;

    let cancelled = false;
    const bound: [string, (...args: unknown[]) => void][] = [];

    const sync = () => {
      const video = videoRef.current;
      if (!video || cancelled) return;
      const percent = video.percentWatched();
      setPercentWatched(percent);
      if (percent >= TRAINING_WATCHED_THRESHOLD - TRAINING_WATCHED_EPSILON) setWatchedThrough(true);
    };

    const unsubscribe = onWistiaVideoReady(step.mediaId, (video) => {
      if (cancelled) return;
      videoRef.current = video;
      setTracking(true);
      video.bind("end", sync);
      bound.push(["end", sync]);
      video.bind("secondchange", sync);
      bound.push(["secondchange", sync]);
      video.bind("percentwatchedchanged", sync);
      bound.push(["percentwatchedchanged", sync]);
      sync();
    });

    const giveUp = () => {
      if (!cancelled && !videoRef.current) setTracking(false);
    };
    const timer = window.setTimeout(giveUp, PLAYER_TIMEOUT_MS);
    void loadWistiaEmbedScript().catch(giveUp);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      unsubscribe();
      const video = videoRef.current;
      bound.forEach(([event, handler]) => video?.unbind(event, handler));
    };
  }, [step.mediaId, alreadyWatched]);

  // Resume where the agent left off: the first video they haven't finished.
  useEffect(() => {
    if (!agentId) return;
    const stored = readWatchedTrainingSteps(agentId);
    setWatched(stored);
    const firstUnwatched = TRAINING_SUB_STEPS.findIndex((entry) => !stored.includes(entry.id));
    setIndex(firstUnwatched === -1 ? TRAINING_SUB_STEPS.length - 1 : firstUnwatched);
  }, [agentId]);

  const goTo = useCallback((next: number) => {
    setIndex(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const advance = useCallback(() => {
    if (!canAdvance) return;
    setWatched(markTrainingStepWatched(agentId, step.id));
    onProgressChange?.();
    if (isLast) {
      onStepComplete?.();
      return;
    }
    goTo(index + 1);
  }, [agentId, canAdvance, goTo, index, isLast, onProgressChange, onStepComplete, step.id]);

  const watchedCount = useMemo(
    () => TRAINING_SUB_STEPS.filter((entry) => watched.includes(entry.id)).length,
    [watched]
  );

  return (
    <section className="space-y-8">
      <motion.header
        className="contents"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <SetupStepHeader
          stepId="training"
          complete={complete}
          action={
            <span className="inline-flex items-center rounded-full bg-surface-overlay px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-content-secondary ring-1 ring-border-default">
              {watchedCount} of {TRAINING_SUB_STEPS.length} watched
            </span>
          }
        />
      </motion.header>

      <SubStepRail index={index} watched={watched} allowSkip={allowSkip} onSelect={goTo} />

      <article className="rounded-card border-2 border-border-default bg-surface-base px-5 py-5 shadow-card sm:px-7 sm:py-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-content-muted">
          Video {index + 1} of {TRAINING_SUB_STEPS.length}
        </p>
        <h3 className="mt-2 font-display text-2xl font-extrabold leading-none tracking-[-0.04em] text-content-primary sm:text-3xl">
          {step.title}
        </h3>
        <p className="mt-3 max-w-3xl text-sm font-medium leading-6 text-content-secondary">{step.summary}</p>

        {step.id === "call-delivery" && (
          <p className="mt-4 rounded-xl border border-status-pending-border bg-surface-raised px-4 py-3 text-sm text-content-primary">
            Your assigned call delivery number is:{" "}
            <span className="font-mono font-semibold">{formatCallDeliveryNumber(callDeliveryNumber)}</span>
          </p>
        )}

        {step.important && (
          <p className="mt-4 rounded-xl border border-status-nigo-border bg-status-nigo-bg px-4 py-3 text-sm font-semibold text-status-nigo-text">
            {step.important}
          </p>
        )}
        {step.notes?.map((note) => (
          <p key={note} className="mt-3 text-sm leading-6 text-content-secondary">
            {note}
          </p>
        ))}

        <div className="mt-5 overflow-hidden rounded-card border-2 border-border-default bg-black">
          <iframe
            // Keyed on the media id so switching sub-steps mounts a fresh
            // player instead of leaving the previous video running.
            key={step.mediaId}
            src={trainingEmbedUrl(step.mediaId)}
            title={`${step.title} training video`}
            // `wistia_embed` is how E-v1 finds the iframe to wire the player
            // API up to; the rest is our own sizing.
            className="wistia_embed aspect-video w-full"
            name="wistia_embed"
            allow="autoplay; fullscreen"
            allowFullScreen
            frameBorder="0"
            scrolling="no"
          />
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-border-subtle pt-5">
          <Button variant="secondary" disabled={index === 0} onClick={() => goTo(index - 1)}>
            <span aria-hidden="true">←</span>
            Back
          </Button>
          <div className="flex flex-wrap items-center gap-4">
            <p
              role="status"
              aria-live="polite"
              className={`text-sm font-semibold ${canAdvance ? "text-status-active-text" : "text-content-secondary"}`}
            >
              {allowSkip && !alreadyWatched && !watchedThrough
                ? "Impersonating — the watch requirement is lifted."
                : alreadyWatched
                ? "You've already watched this one."
                : watchedThrough
                  ? "Watched — you can move on."
                  : tracking === false
                    ? "We can't track this video's playback. Continue once you've watched it."
                    : `Watch the full video to continue · ${Math.floor(percentWatched * 100)}% watched`}
            </p>
            <Button size="lg" disabled={!canAdvance} onClick={advance}>
              {isLast ? "Complete step" : "Next video"}
              <span aria-hidden="true">→</span>
            </Button>
          </div>
        </div>
      </article>
    </section>
  );
}
